import {
  getSelectableRowVariantClassName,
  SELECTABLE_LIST_STYLE_DEFAULT,
  SELECTABLE_ROW_RING_CLASS,
  type SelectableListLayout,
  type SelectableListSize,
  type SelectableListStyle,
  type SelectableListVariant,
} from "./SelectableRow";
import {
  createContext,
  ReactNode,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { InboxIcon, type LucideIcon } from "lucide-react";

import {
  findNearestContextNode,
  HotkeyScope,
  isAncestorOrSelf,
  shouldDeferListFocusReclaim,
  useContextScope,
} from "@/hooks/useContextTree";
import { Spinner } from "@/components/ui/spinner";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

// =============================================================================
// SelectableList (base)
// =============================================================================
// Shared selection shell used by ``SelectableCardList``. Owns loading / empty
// states, focus scope, j/k + arrow keyboard navigation, and active-row
// interaction helpers.
// =============================================================================

export type SelectableListEmptyState = {
  ReactIcon?: LucideIcon;
  title: string;
  description?: string;
};

export type SelectableListItemClickMeta = {
  /**
   * Consecutive clicks this one completes (``MouseEvent.detail``): 2 on the
   * second click of a double-click.
   */
  clickCount: number;
  /**
   * Whether focus was anywhere inside the list scope when the pointer went
   * down (including row descendants). By ``click`` time the list has often
   * stolen focus from elsewhere, so live ``isFocusWithin`` is misleading.
   */
  wasFocusWithin: boolean;
  /**
   * Whether the list scope element itself (not a row descendant) owned focus
   * at pointer-down. Use for toggle/deselect: row-internal focus should
   * refocus the list, not close the selection.
   */
  wasListScopeFocused: boolean;
};

export type SelectableListBaseProps<T> = {
  items: T[];
  loading?: boolean;
  getItemKey: (item: T, index: number) => string;
  /**
   * Where the keyboard stops on a row, in order, by key: a row of several
   * choices stops on each before the next row. By default, its key alone.
   */
  getItemStops?: (item: T, index: number) => string[];
  isItemActive?: (item: T, index: number) => boolean;
  onItemClick?: (
    item: T,
    index: number,
    meta?: SelectableListItemClickMeta,
  ) => void;
  /**
   * Called when j/k or arrow keys move the active row (defaults to
   * ``onItemClick``), with the stop on it they moved to.
   */
  onItemNavigate?: (item: T, index: number, stop: string) => void;
  /** Called when the pointer enters a row (e.g. live preview on hover). */
  onItemHover?: (item: T, index: number) => void;
  /** Called when Space is pressed while a row is active. */
  onItemSpace?: (item: T, index: number, event: KeyboardEvent) => void;
  /** Called when Enter is pressed while a row is active. */
  onItemEnter?: (item: T, index: number, event: KeyboardEvent) => void;
  /** Item key to scroll into view when it changes. */
  scrollToActiveKey?: string | null;
  /**
   * When true, scroll-into-view uses layout offsets instead of transformed
   * bounding rects (pairs with ``SelectableCardList`` ``stickyActiveRow``).
   */
  stickyActiveRow?: boolean;
  /**
   * Extra top clearance when scrolling the active row into view (e.g. sticky
   * group labels that cover the top of the scrollport).
   */
  scrollInsetTop?: number;
  /**
   * Disable the built-in j/k + Arrow Up/Down navigation. Defaults to
   * ``true``. Turn off only if the embedding view already owns these
   * hotkeys and would conflict.
   */
  enableKeyboardNav?: boolean;
  /**
   * Optional ref to a sibling element (typically a search ``<input>``)
   * that should also count as "focus inside the list" for keyboard
   * navigation purposes.
   */
  keyboardSourceRef?: React.RefObject<HTMLElement | null>;
  /** Optional override for when list keyboard navigation should be active. */
  isKeyboardActive?: () => boolean;
  /**
   * Hotkey scope for j/k and arrow navigation. Use ``nearest`` when the list
   * lives inside another context that also registers arrow keys (e.g. NodeLayout).
   */
  keyboardScope?: "global" | "nearest";
  emptyState?: SelectableListEmptyState;
  /** How much room a row takes and what its text is set at. Default ``md``. */
  size?: SelectableListSize;
  /**
   * What the selected row does: ``plain`` a bar at its edge, ``soft`` a fill,
   * ``outlined`` a line round it. Default ``soft``.
   */
  variant?: SelectableListVariant;
  /** ``flush``: edge to edge with hairlines between; ``cards``: inset and rounded. Default ``cards``. */
  layout?: SelectableListLayout;
  /**
   * The row draws its own inset and lines: the wrapper carries only the
   * variant's surface.
   */
  rowChromeless?: boolean;
  /** Forwarded to the outer scroll container as ``data-testid``. */
  "data-testid"?: string;
  /** Pinned below the scroll area (e.g. bulk-selection actions). */
  footer?: ReactNode;
  /** When false, the list sizes to its content instead of filling the parent. */
  fillHeight?: boolean;
};

export type SelectableListContext<T> = {
  items: T[];
  getItemKey: (item: T, index: number) => string;
  isItemActive?: (item: T, index: number) => boolean;
  onItemClick?: (
    item: T,
    index: number,
    meta?: SelectableListItemClickMeta,
  ) => void;
  onRowHover: (item: T, index: number) => void;
  isFocusWithin: boolean;
  activeIndex: number;
  navDirection: "up" | "down" | null;
  setNavDirection: (direction: "up" | "down" | null) => void;
  onRowActivate: (item: T, index: number, clickCount?: number) => void;
  registerRowRef: (index: number, element: HTMLElement | null) => void;
  getRowClassName: (args: {
    isActive: boolean;
    className?: string;
    hasHoverSurface?: boolean;
  }) => string;
};

export function getSelectableRowClassName({
  isActive,
  isFocusWithin,
  hasClick,
  className,
  hasHoverSurface,
  size,
  variant,
  layout,
}: {
  isActive: boolean;
  isFocusWithin: boolean;
  hasClick: boolean;
  className?: string;
  /** Under the pointer, the row takes its variant's surface (by default). */
  hasHoverSurface?: boolean;
  size: SelectableListSize;
  variant: SelectableListVariant;
  layout: SelectableListLayout;
}): string {
  return cn(
    hasClick ? "cursor-pointer" : "cursor-default",
    // The variant's card is the wrapper's to draw either way. ``rowChromeless``
    // says the *row* draws no chrome of its own, not that the list stops
    // drawing its: stripping the border and the shadow here left an outlined
    // list with no card at all, and a chosen row with nothing round it.
    getSelectableRowVariantClassName({
      size,
      variant,
      layout,
      isActive,
      isFocusWithin,
      hasHoverSurface,
    }),
    // The keyboard's ring (``SELECTABLE_ROW_RING_CLASS``), over whatever the
    // variant draws: it says where the keys land, which is not the same as
    // which row is chosen.
    isActive && isFocusWithin
      ? SELECTABLE_ROW_RING_CLASS.on
      : SELECTABLE_ROW_RING_CLASS.off,
    layout === "cards" ? "[outline-offset:0px]" : "[outline-offset:-2px]",
    className,
  );
}

/** The list's style, read by the rows it holds. */
export const SelectableListStyleContext = createContext<SelectableListStyle>(
  SELECTABLE_LIST_STYLE_DEFAULT,
);

export function useSelectableListStyle(): SelectableListStyle {
  return useContext(SelectableListStyleContext);
}

export function useSelectableList<T>({
  items,
  getItemKey,
  getItemStops,
  isItemActive,
  onItemClick,
  onItemNavigate,
  onItemHover,
  onItemSpace,
  onItemEnter,
  scrollToActiveKey,
  scrollInsetTop = 0,
  stickyActiveRow = false,
  enableKeyboardNav = true,
  keyboardSourceRef,
  isKeyboardActive,
  keyboardScope = "global",
  size = SELECTABLE_LIST_STYLE_DEFAULT.size,
  variant = SELECTABLE_LIST_STYLE_DEFAULT.variant,
  layout = SELECTABLE_LIST_STYLE_DEFAULT.layout,
  rowChromeless = false,
  fillHeight = true,
}: Pick<
  SelectableListBaseProps<T>,
  | "items"
  | "getItemKey"
  | "getItemStops"
  | "isItemActive"
  | "onItemClick"
  | "onItemNavigate"
  | "onItemHover"
  | "onItemSpace"
  | "onItemEnter"
  | "scrollToActiveKey"
  | "scrollInsetTop"
  | "stickyActiveRow"
  | "enableKeyboardNav"
  | "keyboardSourceRef"
  | "isKeyboardActive"
  | "keyboardScope"
  | "size"
  | "variant"
  | "layout"
  | "rowChromeless"
  | "fillHeight"
>) {
  const [navDirection, setNavDirection] = useState<"up" | "down" | null>(null);
  const rowRefs = useRef<Array<HTMLElement | null>>([]);
  const wasFocusWithinAtPointerDownRef = useRef(false);
  const wasListScopeFocusedAtPointerDownRef = useRef(false);

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const activeKeyRef = useRef(scrollToActiveKey);
  activeKeyRef.current = scrollToActiveKey;
  const getItemKeyRef = useRef(getItemKey);
  getItemKeyRef.current = getItemKey;
  const getItemStopsRef = useRef(getItemStops);
  getItemStopsRef.current = getItemStops;
  const stopsOf = useCallback(
    (item: T, idx: number) =>
      getItemStopsRef.current?.(item, idx) ?? [
        getItemKeyRef.current(item, idx),
      ],
    [],
  );

  const activeIndex = useMemo(() => {
    if (!scrollToActiveKey) return -1;
    return items.findIndex((item, idx) =>
      stopsOf(item, idx).includes(scrollToActiveKey),
    );
    // Read through their refs, which are this render's: listed so a new one
    // is read again.
  }, [items, scrollToActiveKey, getItemKey, getItemStops, stopsOf]);
  const isItemActiveRef = useRef(isItemActive);
  isItemActiveRef.current = isItemActive;
  const onItemClickRef = useRef(onItemClick);
  onItemClickRef.current = onItemClick;
  const onItemNavigateRef = useRef(onItemNavigate);
  onItemNavigateRef.current = onItemNavigate;
  const onItemHoverRef = useRef(onItemHover);
  onItemHoverRef.current = onItemHover;
  const onItemSpaceRef = useRef(onItemSpace);
  onItemSpaceRef.current = onItemSpace;
  const onItemEnterRef = useRef(onItemEnter);
  onItemEnterRef.current = onItemEnter;
  const isKeyboardNavigatingRef = useRef(false);

  const getActiveIndex = useCallback(() => {
    const list = itemsRef.current;
    const isActive = isItemActiveRef.current;
    const activeKey = activeKeyRef.current;
    return list.findIndex((item, idx) => {
      if (activeKey != null && stopsOf(item, idx).includes(activeKey)) {
        return true;
      }
      if (isActive?.(item, idx)) return true;
      return false;
    });
  }, [stopsOf]);

  const keyboardSourceRefRef = useRef(keyboardSourceRef);
  keyboardSourceRefRef.current = keyboardSourceRef;
  const isKeyboardActiveRef = useRef(isKeyboardActive);
  isKeyboardActiveRef.current = isKeyboardActive;
  const scopeElRef = useRef<HTMLElement | null>(null);
  // Live scope ref object — ``.current`` updates on commit without a re-render,
  // unlike ``scopeElRef`` which is only copied during render.
  const scopeRefLive = useRef<{ current: HTMLElement | null } | null>(null);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const isFocusRelevant = useCallback(() => {
    // When provided, ``isKeyboardActive`` fully owns enablement (override).
    if (isKeyboardActiveRef.current) {
      return isKeyboardActiveRef.current();
    }
    const active = document.activeElement;
    if (!active) return false;
    const scopeEl = scopeRefLive.current?.current ?? scopeElRef.current;
    if (scopeEl && scopeEl.contains(active)) return true;
    const src = keyboardSourceRefRef.current?.current;
    return !!src && src.contains(active);
  }, []);

  // Keyboard nav can run while DOM focus is still on a parent/sibling (e.g.
  // triage ``isKeyboardActive`` override). Reclaim focus + paint chrome so
  // outline matches click-to-focus behavior.
  const claimListFocus = useCallback(() => {
    const el = scopeRefLive.current?.current ?? scopeElRef.current;
    if (el && document.activeElement !== el) {
      el.focus();
    }
    setIsFocusWithin(true);
  }, []);

  const navigate = useCallback(
    (direction: "up" | "down") => {
      const list = itemsRef.current;
      if (list.length === 0) return;
      // Every row's stops in turn: the keys move a stop at a time, and from
      // the last of a row to the first of the next.
      const stops = list.flatMap((item, idx) =>
        stopsOf(item, idx).map((key) => ({ idx, key })),
      );
      const activeKey = activeKeyRef.current;
      const currentIdx = getActiveIndex();
      const onStop = stops.findIndex((stop) => stop.key === activeKey);
      const current =
        onStop >= 0
          ? onStop
          : stops.findIndex((stop) => stop.idx === currentIdx);
      const next =
        direction === "down"
          ? current < 0
            ? 0
            : Math.min(current + 1, stops.length - 1)
          : current <= 0
            ? 0
            : current - 1;
      if (next === current) {
        claimListFocus();
        return;
      }
      const { idx: nextIdx, key: nextKey } = stops[next];
      setNavDirection(direction);
      isKeyboardNavigatingRef.current = true;
      if (onItemNavigateRef.current) {
        onItemNavigateRef.current(list[nextIdx], nextIdx, nextKey);
      } else {
        onItemClickRef.current?.(list[nextIdx], nextIdx);
      }
      claimListFocus();
    },
    [claimListFocus, getActiveIndex, stopsOf],
  );

  const activateSpace = useCallback(
    (event: KeyboardEvent) => {
      const list = itemsRef.current;
      if (list.length === 0) return;
      claimListFocus();
      const currentIdx = getActiveIndex();
      if (currentIdx < 0) return;
      onItemSpaceRef.current?.(list[currentIdx], currentIdx, event);
    },
    [claimListFocus, getActiveIndex],
  );

  const activateEnter = useCallback(
    (event: KeyboardEvent) => {
      const list = itemsRef.current;
      if (list.length === 0) return;
      claimListFocus();
      const currentIdx = getActiveIndex();
      if (currentIdx < 0) return;
      onItemEnterRef.current?.(list[currentIdx], currentIdx, event);
    },
    [claimListFocus, getActiveIndex],
  );

  const {
    Scope,
    ref: scopeRef,
    node: listNode,
  } = useContextScope({
    hotkeys: enableKeyboardNav
      ? [
          {
            id: "selectable-list-down",
            combos: ["ArrowDown", "j"],
            callback: () => navigate("down"),
            scopeCheck:
              keyboardScope === "nearest"
                ? HotkeyScope.nearest
                : HotkeyScope.global,
            isEnabled: isFocusRelevant,
          },
          {
            id: "selectable-list-up",
            combos: ["ArrowUp", "k"],
            callback: () => navigate("up"),
            scopeCheck:
              keyboardScope === "nearest"
                ? HotkeyScope.nearest
                : HotkeyScope.global,
            isEnabled: isFocusRelevant,
          },
          ...(onItemSpace
            ? [
                {
                  id: "selectable-list-space",
                  combos: [" ", "Space"],
                  callback: (event: KeyboardEvent) => activateSpace(event),
                  scopeCheck:
                    keyboardScope === "nearest"
                      ? HotkeyScope.nearest
                      : HotkeyScope.global,
                  isEnabled: isFocusRelevant,
                },
              ]
            : []),
          ...(onItemEnter
            ? [
                {
                  id: "selectable-list-enter",
                  combos: ["Enter"],
                  callback: (event: KeyboardEvent) => activateEnter(event),
                  scopeCheck:
                    keyboardScope === "nearest"
                      ? HotkeyScope.nearest
                      : HotkeyScope.global,
                  isEnabled: isFocusRelevant,
                },
              ]
            : []),
        ]
      : [],
  });
  scopeRefLive.current = scopeRef;
  scopeElRef.current = scopeRef.current;

  const focusScope = useCallback(() => {
    scopeRef.current?.focus();
    setIsFocusWithin(true);
  }, [scopeRef]);

  useEffect(() => {
    const update = () => {
      const active = document.activeElement;
      if (!active) {
        setIsFocusWithin(false);
        return;
      }
      const scopeEl = scopeRef.current;
      if (scopeEl && scopeEl.contains(active)) {
        setIsFocusWithin(true);
        return;
      }
      const src = keyboardSourceRef?.current;
      setIsFocusWithin(!!src && src.contains(active));
    };
    const updateAfterFocusMove = () => {
      // Let the browser finish the focus transfer before reading activeElement
      // (focusout can otherwise clear chrome mid-reclaim).
      requestAnimationFrame(update);
    };
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", updateAfterFocusMove);
    update();
    return () => {
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", updateAfterFocusMove);
    };
  }, [scopeRef, keyboardSourceRef]);

  const hasAutoFocusedRef = useRef(false);
  useEffect(() => {
    if (!enableKeyboardNav) return;
    if (hasAutoFocusedRef.current) return;
    if (items.length === 0) return;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    scopeRef.current?.focus();
    setIsFocusWithin(true);
    hasAutoFocusedRef.current = true;
  }, [enableKeyboardNav, items.length, scopeRef]);

  const onRowActivate = useCallback(
    (item: T, index: number, clickCount = 1) => {
      setNavDirection(null);
      onItemClick?.(item, index, {
        clickCount,
        wasFocusWithin: wasFocusWithinAtPointerDownRef.current,
        wasListScopeFocused: wasListScopeFocusedAtPointerDownRef.current,
      });
      if (shouldDeferListFocusReclaim(scopeRef.current)) {
        return;
      }
      // Defer reclaim so a child that opens a portaled overlay (or focuses
      // itself) can claim focus first. Double rAF matches useContextOverlay
      // autofocus timing.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (shouldDeferListFocusReclaim(scopeRef.current)) {
            return;
          }
          const active = document.activeElement;
          if (
            active instanceof HTMLElement &&
            active !== document.body &&
            active !== scopeRef.current
          ) {
            const activeCtx = findNearestContextNode(active);
            // Portaled overlay opened from within this list owns focus.
            // Sibling panels (e.g. triage suggestions) must not block reclaim.
            if (
              activeCtx &&
              activeCtx !== listNode &&
              isAncestorOrSelf(listNode, activeCtx)
            ) {
              return;
            }
            // Focusable descendant inside the list owns focus
            if (scopeRef.current?.contains(active)) return;
          }
          focusScope();
        });
      });
    },
    [focusScope, listNode, onItemClick, scopeRef],
  );

  const onRowHover = useCallback((item: T, index: number) => {
    if (isKeyboardNavigatingRef.current) return;
    onItemHoverRef.current?.(item, index);
  }, []);

  const onScopePointerMove = useCallback(() => {
    isKeyboardNavigatingRef.current = false;
  }, []);

  const onScopePointerDownCapture = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const scopeEl = scopeElRef.current;
      const active = document.activeElement;
      const src = keyboardSourceRefRef.current?.current;
      wasFocusWithinAtPointerDownRef.current = Boolean(
        (scopeEl && active && scopeEl.contains(active)) ||
        (src && active && src.contains(active)),
      );
      wasListScopeFocusedAtPointerDownRef.current = Boolean(
        scopeEl && active === scopeEl,
      );

      const target = event.target;
      if (!(target instanceof Element) || !scopeEl) {
        return;
      }
      // Empty list chrome / inter-row gap: don't let the focusable scope steal
      // focus — drop focus chrome while keeping the controlled selection.
      const onRow = rowRefs.current.some((row) => row?.contains(target));
      if (!onRow && scopeEl.contains(target)) {
        event.preventDefault();
        if (typeof scopeEl.blur === "function") {
          scopeEl.blur();
        }
      }
    },
    [],
  );

  const registerRowRef = useCallback(
    (index: number, element: HTMLElement | null) => {
      rowRefs.current[index] = element;
    },
    [],
  );

  const scrollInsetTopRef = useRef(scrollInsetTop);
  scrollInsetTopRef.current = scrollInsetTop;
  const stickyActiveRowRef = useRef(stickyActiveRow);
  stickyActiveRowRef.current = stickyActiveRow;

  // The row the keys are on, by where it is now: followed by its key, not its
  // place - the rows round it taken out or put back move it without it being
  // chosen again, and slide it where it goes themselves.
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;
  const isActiveListed = activeIndex >= 0;
  // The row last brought into view: left out a moment and listed again, it is
  // where it was - not brought into view again.
  const shownKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const activeIndex = activeIndexRef.current;
    if (activeIndex < 0) return;
    if (navDirection == null && scrollToActiveKey === shownKeyRef.current) {
      return;
    }
    shownKeyRef.current = scrollToActiveKey ?? null;
    const targetIndex =
      navDirection === "down"
        ? Math.min(activeIndex + 1, itemsRef.current.length - 1)
        : navDirection === "up"
          ? Math.max(activeIndex - 1, 0)
          : activeIndex;

    const scrollActiveIntoView = () => {
      const el = rowRefs.current[targetIndex];
      if (!el) return;

      const topInset = scrollInsetTopRef.current;
      let scrollParent: HTMLElement | null = el.parentElement;
      while (scrollParent) {
        const { overflow, overflowY } = getComputedStyle(scrollParent);
        if (/(auto|scroll|overlay)/.test(overflowY || overflow)) {
          break;
        }
        scrollParent = scrollParent.parentElement;
      }

      if (!scrollParent) {
        el.scrollIntoView({ block: "nearest", behavior: "auto" });
        return;
      }

      if (stickyActiveRowRef.current) {
        let layoutTop = 0;
        let node: HTMLElement | null = el;
        while (node && node !== scrollParent) {
          layoutTop += node.offsetTop;
          node = node.offsetParent as HTMLElement | null;
          if (node && !scrollParent.contains(node)) {
            const parentRect = scrollParent.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            layoutTop = elRect.top - parentRect.top + scrollParent.scrollTop;
            break;
          }
        }
        const layoutBottom = layoutTop + el.offsetHeight;
        const viewTop = scrollParent.scrollTop + topInset;
        const viewBottom = scrollParent.scrollTop + scrollParent.clientHeight;

        if (layoutTop < viewTop) {
          scrollParent.scrollTop = layoutTop - topInset;
        } else if (layoutBottom > viewBottom) {
          scrollParent.scrollTop = layoutBottom - scrollParent.clientHeight;
        }
        return;
      }

      const parentRect = scrollParent.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const visibleTop = parentRect.top + topInset;
      const visibleBottom = parentRect.bottom;

      if (elRect.top < visibleTop) {
        scrollParent.scrollTop -= visibleTop - elRect.top;
      } else if (elRect.bottom > visibleBottom) {
        scrollParent.scrollTop += elRect.bottom - visibleBottom;
      }
    };

    scrollActiveIntoView();
    // Row refs may not be registered until after paint (tab swaps / remounts).
    const frame = requestAnimationFrame(scrollActiveIntoView);
    return () => cancelAnimationFrame(frame);
    // Only follow selection / keyboard navigation — not every ``items`` refresh.
    // Re-scrolling when the parent passes a new ``items`` array (same selection)
    // yanks manual scroll position (see debug logs: navDirection null, Δ≈1600px).
  }, [scrollToActiveKey, isActiveListed, navDirection, scrollInsetTop]);

  const getRowClassName = useCallback(
    ({
      isActive,
      className,
      hasHoverSurface,
    }: {
      isActive: boolean;
      className?: string;
      hasHoverSurface?: boolean;
    }): string =>
      getSelectableRowClassName({
        isActive,
        isFocusWithin,
        hasClick: Boolean(onItemClick),
        className,
        hasHoverSurface,
        size,
        variant,
        layout,
      }),
    [size, variant, layout, isFocusWithin, onItemClick],
  );
  const style = useMemo(
    () => ({ size, variant, layout, rowChromeless }),
    [size, variant, layout, rowChromeless],
  );

  const scopeStyle = useMemo(
    () => ({
      display: "flex",
      flexDirection: "column" as const,
      ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
      outline: "none",
    }),
    [fillHeight],
  );

  const context = useMemo(
    (): SelectableListContext<T> => ({
      items,
      getItemKey,
      isItemActive,
      onItemClick,
      isFocusWithin,
      activeIndex,
      navDirection,
      setNavDirection,
      onRowActivate,
      onRowHover,
      registerRowRef,
      getRowClassName,
    }),
    [
      items,
      getItemKey,
      isItemActive,
      onItemClick,
      isFocusWithin,
      activeIndex,
      navDirection,
      onRowActivate,
      onRowHover,
      registerRowRef,
      getRowClassName,
    ],
  );

  return {
    Scope,
    scopeRef,
    scopeStyle,
    style,
    context,
    onScopePointerMove,
    onScopePointerDownCapture,
  };
}

type SelectableListProps<T> = SelectableListBaseProps<T> & {
  children: (context: SelectableListContext<T>) => ReactNode;
};

export function SelectableList<T>({
  items,
  loading = false,
  emptyState,
  footer,
  "data-testid": dataTestid,
  children,
  ...listProps
}: SelectableListProps<T>) {
  const {
    Scope,
    scopeRef,
    scopeStyle,
    style,
    context,
    onScopePointerMove,
    onScopePointerDownCapture,
  } = useSelectableList({
    items,
    ...listProps,
  });

  if (loading && items.length === 0) {
    return (
      <Scope
        ref={scopeRef}
        tabIndex={-1}
        data-testid={dataTestid}
        style={scopeStyle}
        onPointerMove={onScopePointerMove}
        onPointerDownCapture={onScopePointerDownCapture}
      >
        <div className="flex-1 min-h-0 w-full flex flex-col items-center justify-center pt-16 pb-16">
          <Spinner />
        </div>
        {footer}
      </Scope>
    );
  }

  if (!loading && items.length === 0 && emptyState) {
    const Icon = emptyState.ReactIcon ?? InboxIcon;
    return (
      <Scope
        ref={scopeRef}
        tabIndex={-1}
        data-testid={dataTestid}
        style={scopeStyle}
        onPointerMove={onScopePointerMove}
        onPointerDownCapture={onScopePointerDownCapture}
      >
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center pt-16 pb-16 gap-2">
          <div className="flex items-center justify-center w-12 h-12 rounded-[50%] bg-muted text-muted-foreground mb-4">
            <Icon size={24} />
          </div>
          <Typography level="body-lg" className="text-foreground">
            {emptyState.title}
          </Typography>
          {emptyState.description && (
            <Typography
              level="body-sm"
              className="text-muted-foreground/80 text-center"
            >
              {emptyState.description}
            </Typography>
          )}
        </div>
        {footer}
      </Scope>
    );
  }

  return (
    <Scope
      ref={scopeRef}
      tabIndex={-1}
      data-testid={dataTestid}
      style={scopeStyle}
      onPointerMove={onScopePointerMove}
      onPointerDownCapture={onScopePointerDownCapture}
    >
      <SelectableListStyleContext.Provider value={style}>
        {children(context)}
      </SelectableListStyleContext.Provider>
      {footer}
    </Scope>
  );
}
